/**
 * Export Service
 * 
 * Provides functions to export data to CSV, Excel, and JSON formats
 */

export interface ExportOptions {
  filename?: string;
  includeHeaders?: boolean;
  fields?: string[]; // selected columns (keys)
  format?: 'csv' | 'json';
  chunkSize?: number; // split large exports
}

// Convert array of objects to CSV string
export const exportToCSV = (data: any[], options: ExportOptions = {}): string => {
  const includeHeaders = options.includeHeaders !== false;
  const fields = options.fields && options.fields.length > 0 ? options.fields : undefined;
  
  if (data.length === 0) return '';
  
  // Get headers from first object
  const headers = includeHeaders ? (fields || Object.keys(data[0])) : [];
  
  // Convert data to rows
  const rows = data.map(item => {
    const values = (fields ? fields.map(k => item[k]) : Object.values(item));
    return values.map(value => {
      // Handle commas, quotes, and newlines in values
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    });
  });
  
  // Combine headers and rows
  const lines = includeHeaders ? [headers.join(','), ...rows.map(row => row.join(','))] : rows.map(row => row.join(','));
  
  return lines.join('\n');
};

// Download file with given content and filename
export const downloadFile = (content: string, filename: string, mimeType: string = 'text/plain'): void => {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Advanced export with fields/format/chunking
export const exportDataAdvanced = (
  data: any[],
  {
    filename,
    includeHeaders = true,
    fields,
    format = 'csv',
    chunkSize,
  }: ExportOptions = {}
): void => {
  const base = filename || `export_${new Date().toISOString().split('T')[0]}`;
  if (format === 'json') {
    const jsonContent = JSON.stringify(
      fields && fields.length ? data.map(d => Object.fromEntries(fields.map(f => [f, d[f]]))) : data,
      null,
      2
    );
    downloadFile(jsonContent, `${base}.json`, 'application/json');
    return;
  }
  // CSV
  const toSlice = (arr: any[], size: number) => {
    const parts: any[][] = [];
    for (let i = 0; i < arr.length; i += size) parts.push(arr.slice(i, i + size));
    return parts;
  };
  if (chunkSize && chunkSize > 0 && data.length > chunkSize) {
    const chunks = toSlice(data, chunkSize);
    chunks.forEach((chunk, idx) => {
      const csv = exportToCSV(chunk, { includeHeaders, fields });
      downloadFile(csv, `${base}_part${idx + 1}.csv`, 'text/csv');
    });
  } else {
    const csv = exportToCSV(data, { includeHeaders, fields });
    downloadFile(csv, `${base}.csv`, 'text/csv');
  }
};

// Export data to CSV file
export const exportDataToCSV = (data: any[], filename?: string): void => {
  const csvContent = exportToCSV(data, { includeHeaders: true });
  const defaultFilename = `export_${new Date().toISOString().split('T')[0]}.csv`;
  downloadFile(csvContent, filename || defaultFilename, 'text/csv');
};

// Export data to JSON file
export const exportDataToJSON = (data: any[], filename?: string): void => {
  const jsonContent = JSON.stringify(data, null, 2);
  const defaultFilename = `export_${new Date().toISOString().split('T')[0]}.json`;
  downloadFile(jsonContent, filename || defaultFilename, 'application/json');
};

// Export bookings data
export const exportBookings = (bookings: any[]): void => {
  const processedBookings = bookings.map(booking => ({
    'Booking ID': booking.id,
    'Customer ID': booking.customerId,
    'Customer Name': booking.customerName,
    'Service Type': booking.serviceType,
    'Date': booking.scheduledDate?.toDate?.() ? booking.scheduledDate.toDate().toLocaleDateString() : booking.scheduledDate,
    'Status': booking.status,
    'Amount': `$${(booking.amount / 100).toFixed(2)}`,
    'Address': booking.address,
    'Technician ID': booking.technicianId || 'Unassigned',
    'Payment Status': booking.paymentStatus || 'N/A',
  }));
  
  exportDataToCSV(processedBookings, `bookings_export_${new Date().toISOString().split('T')[0]}.csv`);
};

// Export users data
export const exportUsers = (users: any[]): void => {
  const processedUsers = users.map(user => ({
    'User ID': user.id,
    'Email': user.email,
    'First Name': user.firstName || 'N/A',
    'Last Name': user.lastName || 'N/A',
    'Role': user.role,
    'Phone': user.phone || 'N/A',
    'Active': user.isActive ? 'Yes' : 'No',
    'Created': user.createdAt?.toDate?.() ? user.createdAt.toDate().toLocaleDateString() : 'N/A',
  }));
  
  exportDataToCSV(processedUsers, `users_export_${new Date().toISOString().split('T')[0]}.csv`);
};

// Export payments data
export const exportPayments = (payments: any[]): void => {
  const processedPayments = payments.map(payment => ({
    'Payment ID': payment.id,
    'Booking ID': payment.bookingId,
    'Customer ID': payment.customerId,
    'Customer Name': payment.customerName || 'N/A',
    'Amount': `$${(payment.amount / 100).toFixed(2)}`,
    'Status': payment.status,
    'Payment Method': payment.paymentMethod || 'N/A',
    'Transaction ID': payment.transactionId || 'N/A',
    'Date': payment.createdAt?.toDate?.() ? payment.createdAt.toDate().toLocaleDateString() : payment.createdAt,
  }));
  
  exportDataToCSV(processedPayments, `payments_export_${new Date().toISOString().split('T')[0]}.csv`);
};

// Export technicians data
export const exportTechnicians = (technicians: any[]): void => {
  const processedTechnicians = technicians.map(tech => ({
    'Technician ID': tech.id,
    'Name': `${tech.firstName} ${tech.lastName}`,
    'Email': tech.email,
    'Phone': tech.phone,
    'Hourly Rate': `$${tech.hourlyRate || 0}/hr`,
    'Active': tech.isActive ? 'Yes' : 'No',
    'Online': tech.isOnline ? 'Yes' : 'No',
    'Rating': (tech.rating || 0).toFixed(1),
    'Total Reviews': tech.totalReviews || 0,
  }));
  
  exportDataToCSV(processedTechnicians, `technicians_export_${new Date().toISOString().split('T')[0]}.csv`);
};

// Export to Excel (using CSV with .xlsx-like formatting)
export const exportToExcel = (data: any[], filename?: string): void => {
  // For a simple implementation, we'll use CSV with tab delimiter
  // A more robust solution would require a library like xlsx or exceljs
  const processedData = data.map(item => Object.values(item).join('\t'));
  const content = Object.keys(data[0] || {}).join('\t') + '\n' + processedData.join('\n');
  const defaultFilename = `export_${new Date().toISOString().split('T')[0]}.xlsx`;
  downloadFile(content, filename || defaultFilename, 'application/vnd.ms-excel');
};

